import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import X from 'lucide-react/dist/esm/icons/x';
import SearchBar from '../search-bar';
import Spinner from '../spinner';
import LoadError from '../load-error';
import Notifications from '../notifications';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import DesktopNotificationsPrompt from './desktop-notifications-prompt';
import FayeService from '~/modules/services/faye-service';
import NotificationService from '~/modules/services/notification-service';
import {
	CheckboxLabel, Container, FiltersContainer, Header, MenuContainer, Overlay
} from './notifications-panel.css.js';
import { Button as ShadcnButton } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';

import useNotificationsStore, {
	selectNotifications,
	selectSummary,
	selectIsLoadingNew,
	selectIsLoadingMore,
	selectIsUpdating,
	selectIsError,
	selectHasMore
} from '~/stores/use-notifications-store';
import { useShallow } from 'zustand/react/shallow';

import useAuth from '~/modules/hooks/use-auth';
import useDesktopNotifications from '~/modules/hooks/use-desktop-notifications';
import { useQuery } from '@tanstack/react-query';
import ProjectService from '~/modules/services/project-service';

const TABS = [
	{
		key: 'workRequest',
		label: 'Orders'
	},
	{
		key: 'workflow',
		label: 'Workflow'
	},
	{
		key: 'embargo',
		label: 'Embargoes'
	},
	{
		key: 'other',
		label: 'General'
	}
];

function NotificationsPanel(props) {
	const {
		isOpen = false,
		onMenuClose,
		isLoading
	} = props;

	const notifications = useNotificationsStore(useShallow(selectNotifications));
	const summary = useNotificationsStore(selectSummary);
	const isLoadingNew = useNotificationsStore(selectIsLoadingNew);
	const isLoadingMore = useNotificationsStore(selectIsLoadingMore);
	const isUpdating = useNotificationsStore(selectIsUpdating);
	const isError = useNotificationsStore(selectIsError);
	const hasMore = useNotificationsStore(selectHasMore);
	const manualUpdateNotifications = useNotificationsStore((state) => state.manualUpdateNotifications);
	const fetchNewNotifications = useNotificationsStore((state) => state.fetchNewNotifications);
	const fetchMoreNotifications = useNotificationsStore((state) => state.fetchMoreNotifications);
	const markRead = useNotificationsStore((state) => state.markRead);
	const markUnread = useNotificationsStore((state) => state.markUnread);
	const markAllRead = useNotificationsStore((state) => state.markAllRead);

	let tabs = TABS;

	const { data: { nodes: projects = [] } = {} } = useQuery(
		['getProjects', { archived: false }],
		() => ProjectService.getAllProjects({ archived: false }),
		{ enabled: isOpen }
	);
	const { session } = useAuth();
	const desktopNotifications = useDesktopNotifications();
	const { notify } = desktopNotifications;
	const [isLiveUpdating, setIsLiveUpdating] = useState(false);
	const [filters, setFilters] = useState({ type: TABS[0].key, q: null, read: 1 });
	const searchRef = React.useRef(null);

	// @todo this really should have been handled by the server
	let decoratedNotifications = notifications.map((notification) => {
		let defaultLogo = window.location.origin + '/img/apple/apple-touch-icon-72x72.png';
		if(notification.event?.projects?.[0]?._id) {
			let project = projects?.find((item) => item._id === notification.event.projects[0]._id);
			notification.poster = project?.$links?.logo ?? defaultLogo;
		}
		else {
			notification.poster = defaultLogo;
		}
		return notification;
	});

	useEffect(() => {
		NotificationService.onRouteChange(() => onMenuClose());
	}, []);

	useEffect(() => {
		if(!isOpen) {
			return;
		}
		const handleKeyDown = (e) => {
			const tag = e.target.tagName;
			const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;
			if(e.key === '/' && !isTyping) {
				e.preventDefault();
				searchRef.current?.focus();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen]);

	useEffect(() => {
		loadNew();
	}, [filters]);

	useEffect(() => {
		let unsubscribes = TABS.map((tab) => {
			return FayeService.subscribe(
				`/notifications/${session?._id}/${tab.key}`, (notification) => {
					notify('Pixwel', {
						body: notification.title,
						icon: `${window.location.origin}/img/apple/apple-touch-icon-57x57.png`
					});
					manualUpdateNotifications({ type: tab.key, notification });
					setIsLiveUpdating(true);
					updateTabLabels();
				}
			);
		});

		return () => {
			unsubscribes.map((unsubscribe) => {
				return unsubscribe();
			});
		};
	}, [isLiveUpdating]);

	const updateTabLabels = () => {
		if(summary) {
			tabs = Object.keys(summary)
				.filter((key) => TABS?.some((item) => item.key === key))
				.map((key) => {
					let tab = _.clone(TABS.find((item) => item.key === key));
					let unread = summary[key].unread.toString();
					tab.label = `${tab.label} (${unread})`;
					return tab;
				});
		}
	};
	updateTabLabels();

	const loadNew = () => {
		fetchNewNotifications({ filters: prepareFilters(filters) });
	};

	const prepareFilters = (filters) => {
		const removeEmpty = (obj) => {
			Object.keys(obj).forEach((key) => (obj[key] === null || obj[key] === '' || obj[key] === 1) && delete obj[key]);
		};
		let cleanFilters = _.clone(filters);
		removeEmpty(cleanFilters);
		return cleanFilters;
	};

	const handleFilterChange = (type, val) => {
		setFilters({
			...filters,
			[type]: val
		});
	};

	const handleNotificationClick = (notification) => {
		if( !notification.read ) {
			markRead({ type: filters.type, notification });
		}
		onMenuClose();
	};

	const toNotificationSref = (notification) => {
		if( filters.type === 'workRequest' ) {
			return {
				state: 'work-request',
				params: {
					id: notification.event?.workRequest?._id
				}
			};
		}

		if( filters.type === 'embargo' ) {
			return {
				state: 'asset',
				params: {
					projectId: notification.event.projects[0]?._id,
					assetId: notification.event.assets[0]?._id
				}
			};
		}

		if( filters.type === 'workflow' ) {
			return {
				state: 'asset',
				params: {
					projectId: notification.event?.projects?.[0]?._id,
					assetId: notification.event?.assets?.[0]?._id
				}
			};
		}

		if( filters.type === 'other' ) {
			switch( notification.event.type ) {
				case 'is':
				case 'ip':
				case 'iw':
				case 'ic':
				case 'ir':
					return {
						state: 'ingest.view',
						params: {
							id: notification.event.ingest
						}
					};

				case 'gajs':
				case 'gajc':
				case 'gaje':
				case 'gaujs':
				case 'gaujc':
				case 'gauje':
					return {
						state: 'asset',
						params: {
							projectId: notification.event.projects[0]?._id,
							assetId: notification.event.assets[0]?._id
						}
					};
				case 'sc':
					// handles both 2.x and 3.x share URLs
					return {
						state: 'share-view',
						params: {
							id: notification.event.urls?.playlist?.replace('?index=0', '').split('/')[4]
						}
					};

				default:
					return {
						state: 'asset',
						params: {
							projectId: notification.event.projects[0]?._id,
							assetId: notification.event.assets[0]?._id
						}
					};
			}
		}
	};

	const handleReadStatusClick = (notification) => {

		if(notification.read) {
			markUnread({ type: filters.type, notification });
		}
		else {
			markRead({ type: filters.type, notification });
		}
	};

	const handleOnLoadMoreClick = () => {
		fetchMoreNotifications({ filters: prepareFilters(filters) });
	};

	const handleMarkAllRead = () => {
		markAllRead({ type: filters.type });
	};

	const renderFilters = () => (
		<React.Fragment>
			<SearchBar onInput={(val) => handleFilterChange('q', val)} inputRef={searchRef} />

			<FiltersContainer>
				<div className="flex">
					<label htmlFor="filterUnread" className="mb-0 mr-8 font-bold">
						<Checkbox
							id="filterUnread"
							checked={!filters.read}
							onCheckedChange={() => handleFilterChange('read', +!filters.read)}
							data-testid="unread-filter"
						/>
						<CheckboxLabel>Unread Only</CheckboxLabel>
					</label>
				</div>

				<ShadcnButton
					variant="secondary"
					size="sm"
					disabled={isUpdating}
					onClick={() => handleMarkAllRead()}
				>
					{isUpdating ? 'Updating...' : 'Mark all as Read'}
				</ShadcnButton>
			</FiltersContainer>

			<Tabs
				value={filters.type}
				onValueChange={(type) => handleFilterChange('type', type)}
				data-cy="tabs-notificationType"
				data-testid="tabs-notificationType"
			>
				<TabsList variant="line">
					{tabs.map((tab) => (
						<TabsTrigger
							key={tab.key}
							value={tab.key}
							data-cy="tabs-notificationType-tab"
							data-testid={`tabs-${tab.key}-tab`}
						>
							{tab.label}
						</TabsTrigger>
					))}
				</TabsList>
			</Tabs>
		</React.Fragment>
	);

	return (
		<Container isOpen={isOpen}>
			<Overlay
				isOpen={isOpen}
				onClick={() => onMenuClose()}
			/>
			<MenuContainer
				isOpen={isOpen}
				data-testid="notifications-menu"
			>
				<Header>
					<div className="flex items-center justify-between gap-4">
						<h3 className="mb-3 font-bold">Notifications</h3>
						<ShadcnButton
							type="button"
							variant="ghost"
							size="icon"
							onClick={() => onMenuClose()}
							className="px-notifications-close-btn mt-[-0.25rem] text-white"
							aria-label="Close notifications"
						>
							<X className="h-4 w-4" />
						</ShadcnButton>
					</div>

					<DesktopNotificationsPrompt
						permission={desktopNotifications.permission}
						isSupported={desktopNotifications.isSupported}
						isRequesting={desktopNotifications.isRequesting}
						onEnable={desktopNotifications.request}
					/>

					{renderFilters()}
				</Header>

				{ isError
					? (
						<LoadError onButtonClick={loadNew}></LoadError>
					)
					: null }

				{ isLoading
					? (
						<Spinner />
					)
					: (
						<Notifications
							hasMore={hasMore}
							isError={isError}
							isLoadingNew={isLoadingNew}
							isLoadingMore={isLoadingMore}
							notifications={decoratedNotifications}
							onLoadMore={handleOnLoadMoreClick}
							onReadStatusClick={handleReadStatusClick}
							onNotificationClick={handleNotificationClick}
							toNotification={toNotificationSref}
						/>
					)}
			</MenuContainer>
		</Container>
	);
}

NotificationsPanel.propTypes = {
	isOpen: PropTypes.bool,
	onMenuClose: PropTypes.func,
	isLoading: PropTypes.bool
};

export default NotificationsPanel;
